import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Space, Button, message, Popconfirm, Divider, Typography, Table, Modal, Form, Input, Switch, Select, Spin, Result } from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, SoundOutlined, CopyOutlined, EditOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { GET_VOCAL } from '../../graphql/queries';
import { DELETE_VOCAL, UPDATE_VOCAL } from '../../graphql/mutation';
import './VocalDetail.css';

const { Title, Text } = Typography;

const VocalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [form] = Form.useForm();

  const { loading, data, error, refetch } = useQuery(GET_VOCAL, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [deleteVocal] = useMutation(DELETE_VOCAL, {
    onCompleted: () => {
      message.success('Vocal deleted successfully');
      navigate('/vocals');
    },
    onError: (error) => {
      message.error(`Failed to delete vocal: ${error.message}`);
    }
  });

  const [updateVocal] = useMutation(UPDATE_VOCAL, {
    onCompleted: () => {
      message.success('Vocal updated successfully');
      setIsEditModalVisible(false);
      refetch();
    },
    onError: (error) => {
      message.error(`Failed to update vocal: ${error.message}`);
    }
  });

  const handleDelete = () => {
    deleteVocal({ variables: { id } });
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showEditModal = () => {
    form.setFieldsValue({
      title: vocal?.title || '',
      vocalGender: vocal?.vocalGender || 'male',
      duration: vocal?.duration || undefined,
      isVisible: vocal?.isVisible ?? true,
    });
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      await updateVocal({
        variables: {
          id,
          input: values
        }
      });
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Result
        status="error"
        title="Failed to load vocal"
        subTitle={error.message}
        extra={[
          <Button key="retry" type="primary" onClick={() => refetch()}>
            Retry
          </Button>,
          <Button key="back" onClick={() => navigate('/vocals')}>
            Back to Vocals
          </Button>
        ]}
      />
    );
  }

  const vocal = data?.getVocal;

  if (!vocal) {
    return (
      <Result
        status="404"
        title="Vocal not found"
        subTitle="This vocal may have been deleted or does not exist."
        extra={
          <Button type="primary" onClick={() => navigate('/vocals')}>
            Back to Vocals
          </Button>
        }
      />
    );
  }

  const audioColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title) => title || 'Untitled'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          SUCCEEDED: 'success',
          FAILED: 'error',
          RUNNING: 'processing',
          IN_QUE: 'warning',
          PENDING: 'default',
        };
        return <Tag color={colors[status]}>{status}</Tag>;
      }
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => duration ? `${duration}s` : '-'
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => dayjs(createdAt).format('YYYY-MM-DD HH:mm')
    },
    {
      title: 'Audio',
      dataIndex: 'url',
      key: 'url',
      render: (url) => url ? (
        <audio controls style={{ maxWidth: 200 }}>
          <source src={url} type="audio/mpeg" />
        </audio>
      ) : '-'
    }
  ];

  return (
    <div className="vocal-detail-page">
      <Card>
        <Space style={{ marginBottom: 24 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/vocals')}
          >
            Back to Vocals
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={showEditModal}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure to delete this vocal?"
            onConfirm={handleDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>

        <Title level={3}>
          <SoundOutlined /> {vocal.title || 'Untitled Vocal'}
        </Title>

        <Divider />

        <Descriptions bordered column={2}>
          <Descriptions.Item label="ID">
            <Space>
              <Text copyable>{vocal.id}</Text>
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(vocal.id, 'ID')}
              />
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="Created">
            {dayjs(vocal.createdAt).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>

          <Descriptions.Item label="User">
            {vocal.user?.email || 'Unknown'}
            {vocal.user?.rcUserId && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                RC ID: {vocal.user.rcUserId}
              </div>
            )}
          </Descriptions.Item>

          <Descriptions.Item label="Premium Status">
            <Tag color={vocal.user?.isPremium ? 'gold' : 'default'}>
              {vocal.user?.isPremium ? 'Premium' : 'Free'}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Gender">
            <Tag color={vocal.vocalGender === 'male' ? 'blue' : 'pink'}>
              {vocal.vocalGender === 'male' ? 'Male' : 'Female'}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Visibility">
            <Tag color={vocal.isVisible ? 'success' : 'default'}>
              {vocal.isVisible ? 'Visible' : 'Hidden'}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label="Duration">
            {vocal.duration ? `${vocal.duration}s` : 'N/A'}
          </Descriptions.Item>

          <Descriptions.Item label="Vocal URL">
            {vocal.vocalUrl ? (
              <Space>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(vocal.vocalUrl, `vocal-${vocal.id}.mp3`)}
                >
                  Download
                </Button>
                <audio controls style={{ maxWidth: 300 }}>
                  <source src={vocal.vocalUrl} type="audio/mpeg" />
                </audio>
              </Space>
            ) : 'N/A'}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Generated Audios ({vocal.audios?.length || 0})</Divider>

        <Table
          columns={audioColumns}
          dataSource={vocal.audios || []}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <Modal
        title="Edit Vocal"
        open={isEditModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => setIsEditModalVisible(false)}
        okText="Save"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Title"
            name="title"
            rules={[{ max: 200, message: 'Title cannot exceed 200 characters' }]}
          >
            <Input placeholder="Enter vocal title" />
          </Form.Item>

          <Form.Item
            label="Gender"
            name="vocalGender"
            rules={[{ required: true, message: 'Please select gender' }]}
          >
            <Select>
              <Select.Option value="male">Male</Select.Option>
              <Select.Option value="female">Female</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Duration (seconds)"
            name="duration"
          >
            <Input type="number" placeholder="Enter duration in seconds" />
          </Form.Item>

          <Form.Item
            label="Visible"
            name="isVisible"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VocalDetail;
